import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TablePagination,
    TextField,
    InputAdornment,
    LinearProgress,
} from "@mui/material";
import {
    FilterList,
    Search,
    KeyboardArrowDown,
    Circle,
    FilterListOff,
} from "@mui/icons-material";
import api from "../api/api";
import CustomSnackbar from "../components/CustomSnackbar";
import PatientDetailsDialog from "./BookingManagement/PatientDetailsDialog";
import en_ja from "../en-ja-map";

const PatientManagement = () => {
    const [patients, setPatients] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [patientType, setPatientType] = useState("");
    const [gender, setGender] = useState("");
    const [dateRange, setDateRange] = useState("");
    const [lastVisit, setLastVisit] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const user = JSON.parse(localStorage.getItem("user"));
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [selectedPatientData, setSelectedPatientData] = useState(null);
    const [originalPatients, setOriginalPatients] = useState([]);

    useEffect(() => {
        fetchPatients();
    }, [page, rowsPerPage]);
    useEffect(() => {
        applyFilters();
    }, [patients, patientType, gender, dateRange, lastVisit, searchTerm]);

    const fetchPatients = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/v1/clinic/${user.id}/appointments`);
            if (response && response.data && response.data.data) {
                const rawData = response.data.data;
                setOriginalPatients(rawData);
                const transformedData = rawData.map((appointment) => ({
                    id: appointment.id,
                    name: appointment["user.name"] || (en_ja["Unknown Patient"] || "Unknown Patient"),
                    patientId: `PAT${String(appointment["user.id"]).padStart(3, "0")}`,
                    email: appointment["user.email"] || (en_ja["No email"] || "No email"),
                    contact: appointment["user.phone_number"] || (en_ja["No contact"] || "No contact"),
                    dateOfBirth: "N/A",
                    gender: appointment["user.gender"] || (en_ja["Not specified"] || "Not specified"),
                    lastVisit: appointment.appointment_time || "N/A",
                    consultType:
                        appointment.appointment_type === "online"
                            ? (en_ja["Online"] || "Online")
                            : (en_ja["Offline"] || "Offline"),
                    status: appointment.status,
                    appointmentDate: appointment.appointment_date,
                    appointmentTime: appointment.appointment_time,
                }));
                setPatients(transformedData);
                setTotalCount(transformedData.length);
            } else {
                setPatients([]);
                setTotalCount(0);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching patients:", error);
            setSnackbarMessage(en_ja["Error fetching patients"] || "Error fetching patients");
            setSnackbarType("error");
            setSnackbarOpen(true);
            setPatients([]);
            setTotalCount(0);
            setLoading(false);
        }
    };

    const handlePatientClick = (appointment) => {
        const transformedPatient = {
            user: {
                id: appointment["user.id"] || "N/A",
                name: appointment["user.name"] || appointment.clinic_checkin_data?.full_name,
                email: appointment["user.email"] || appointment.clinic_checkin_data?.email,
                phone_number: appointment["user.phone_number"] || "N/A"
            },
            clinic_checkin_data: appointment.clinic_checkin_data || {}
        };
        setSelectedPatientData(transformedPatient);
        setPatientDialogOpen(true);
    };

    const applyFilters = () => {
        let filtered = [...patients];
        if (searchTerm) {
            filtered = filtered.filter(
                (patient) =>
                    (patient.name &&
                        patient.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (patient.patientId &&
                        patient.patientId
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())) ||
                    (patient.email &&
                        patient.email.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        if (gender && gender !== "") {
            filtered = filtered.filter((patient) => patient.gender === gender);
        }
        if (patientType && patientType !== "") {
            filtered = filtered.filter(
                (patient) => patient.consultType === patientType
            );
        }
        setFilteredPatients(filtered);
    };

    const handleResetFilters = () => {
        setPatientType("");
        setGender("");
        setDateRange("");
        setLastVisit("");
        setSearchTerm("");
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const getInitials = (name) => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();
    };
    const getConsultTypeColor = (type) => {
        return type === (en_ja["Online"] || "Online") ? "#4caf50" : "#ff9800";
    };
    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
        setSnackbarType("success");
    };
    const paginatedPatients = filteredPatients.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box
            sx={{
                p: 3,
                maxWidth: "100%",
                backgroundColor: "white",
                minHeight: "100vh",
            }}
        >
            <CustomSnackbar
                isOpen={snackbarOpen}
                message={snackbarMessage}
                onClose={handleCloseSnackbar}
                type={snackbarType}
            />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: "1px solid #e9ecef",
                    backgroundColor: "white",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                        boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                        transform: "translateY(-4px)",
                    },
                }}
            >
                {loading && (
                    <Box sx={{ width: "100%", mb: 2 }}>
                        <LinearProgress />
                    </Box>
                )}
                <Box
                    sx={{
                        p: 3,
                        borderBottom: "1px solid #e9ecef",
                        background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
                    }}
                >
                    <TextField
                        placeholder={en_ja["Search patients..."] || "Search patients..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search
                                        sx={{
                                            color: "#6c757d",
                                            fontSize: "20px",
                                            transition: "color 0.2s ease",
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "350px",
                            "& .MuiOutlinedInput-root": {
                                fontSize: "14px",
                                backgroundColor: "white",
                                borderRadius: "12px",
                                transition: "all 0.2s ease",
                                "& fieldset": {
                                    borderColor: "#e9ecef",
                                    transition: "border-color 0.2s ease",
                                },
                                "&:hover": {
                                    transform: "scale(1.02)",
                                    "& fieldset": { borderColor: "#dc3545" },
                                    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                                        color: "#dc3545",
                                    },
                                },
                                "&.Mui-focused": {
                                    transform: "scale(1.02)",
                                    "& fieldset": { borderColor: "#dc3545", borderWidth: "2px" },
                                    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                                        color: "#dc3545",
                                    },
                                },
                            },
                        }}
                    />
                </Box>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: "#f8f9fa",
                                    "&:hover": { backgroundColor: "#f1f3f4" },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "#495057",
                                        py: 2,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    #
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Name"] || "Name"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Patient ID"] || "Patient ID"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Email"] || "Email"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Contact"] || "Contact"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Date of Birth"] || "Date of Birth"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Gender"] || "Gender"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Last Visit"] || "Last Visit"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#495057", py: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    {en_ja["Consult Type"] || "Consult Type"}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedPatients.map((patient, index) => (
                                <TableRow
                                    key={patient.id}
                                    onClick={() => {
                                        const originalPatient = originalPatients.find(p => p.id === patient.id);
                                        handlePatientClick(originalPatient);
                                    }}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.2s ease-in-out",
                                        borderBottom: "1px solid #e9ecef",
                                        "&:hover": {
                                            backgroundColor: "#f8f9fa",
                                            transform: "translateX(4px)",
                                            boxShadow: "4px 0 0 0 #dc3545",
                                            "& .patient-avatar": {
                                                transform: "scale(1.1)",
                                                boxShadow: "0 4px 12px rgba(220, 53, 69, 0.3)",
                                            },
                                            "& .patient-name": { color: "#dc3545" },
                                            "& .consult-dot": { transform: "scale(1.3)" },
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Avatar
                                                className="patient-avatar"
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    fontSize: "12px",
                                                    fontWeight: "600",
                                                    backgroundColor: "#dc3545",
                                                    color: "white",
                                                    transition: "all 0.2s ease",
                                                    boxShadow: "0 2px 8px rgba(220, 53, 69, 0.2)",
                                                }}
                                            >
                                                {patient.name ? getInitials(patient.name) : "U"}
                                            </Avatar>
                                            <Typography
                                                className="patient-name"
                                                variant="body2"
                                                fontWeight="500"
                                                color="#495057"
                                                sx={{
                                                    transition: "color 0.2s ease",
                                                    textTransform: 'capitalize',
                                                    cursor: 'pointer',
                                                    '&:hover': { color: '#dc3545' }
                                                }}
                                            >
                                                {patient.name || (en_ja["Unknown Patient"] || "Unknown Patient")}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: "monospace",
                                                backgroundColor: "#f8f9fa",
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: "4px",
                                                display: "inline-block",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                            }}
                                        >
                                            {patient.patientId || "N/A"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                            {patient.email || (en_ja["No email"] || "No email")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "13px" }}>
                                            {patient.contact || (en_ja["No contact"] || "No contact")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        {patient.dateOfBirth || "N/A"}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        <Chip
                                            label={patient.gender === '1' ? (en_ja["Female"] || "Female") : (en_ja["Male"] || "Male") || (en_ja["Not specified"] || "Not specified")}
                                            size="small"
                                            sx={{
                                                backgroundColor:
                                                    patient.gender === "Male"
                                                        ? "#e3f2fd"
                                                        : patient.gender === "Female"
                                                            ? "#fce4ec"
                                                            : "#f5f5f5",
                                                color:
                                                    patient.gender === "Male"
                                                        ? "#1976d2"
                                                        : patient.gender === "Female"
                                                            ? "#c2185b"
                                                            : "#666",
                                                fontWeight: "600",
                                                fontSize: "11px",
                                                height: "22px",
                                                "& .MuiChip-label": { px: 1 },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "14px", color: "#495057", py: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "500", color: "#28a745" }}>
                                            {patient.lastVisit || "N/A"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Circle
                                                className="consult-dot"
                                                sx={{
                                                    fontSize: "8px",
                                                    color: getConsultTypeColor(
                                                        patient.consultType || (en_ja["Offline"] || "Offline")
                                                    ),
                                                    transition: "transform 0.2s ease",
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: "12px",
                                                    fontWeight: "600",
                                                    color: getConsultTypeColor(
                                                        patient.consultType || (en_ja["Offline"] || "Offline")
                                                    ),
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                }}
                                            >
                                                {patient.consultType || (en_ja["Offline"] || "Offline")}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 3,
                        borderTop: "1px solid #e9ecef",
                        background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
                    }}
                >
                    <Typography
                        variant="body2"
                        color="#6c757d"
                        sx={{ fontSize: "14px", fontWeight: "500" }}
                    >
                        {(en_ja["Showing"] || "Showing")} {Math.min(page * rowsPerPage + 1, filteredPatients.length)}-
                        {Math.min((page + 1) * rowsPerPage, filteredPatients.length)} {en_ja["of"] || "of"}{" "}
                        {filteredPatients.length}
                    </Typography>
                    <TablePagination
                        component="div"
                        count={filteredPatients.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={en_ja["Rows per page:"] || "Rows per page:"}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                            "& .MuiTablePagination-displayedRows": {
                                fontSize: "14px",
                                fontWeight: "500",
                            },
                            "& .MuiTablePagination-selectLabel": {
                                fontSize: "14px",
                                fontWeight: "500",
                            },
                            "& .MuiTablePagination-select": {
                                borderRadius: "8px",
                                transition: "all 0.2s ease",
                                "&:hover": { backgroundColor: "rgba(220, 53, 69, 0.04)" },
                            },
                            "& .MuiTablePagination-actions": {
                                "& .MuiIconButton-root": {
                                    borderRadius: "8px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(220, 53, 69, 0.04)",
                                        color: "#dc3545",
                                        transform: "scale(1.1)",
                                    },
                                },
                            },
                        }}
                    />
                </Box>
            </Paper>

            {!loading && filteredPatients.length === 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 8,
                        px: 4,
                        mt: 4,
                        backgroundColor: "white",
                        borderRadius: "16px",
                        border: "1px solid #e9ecef",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "rgba(220, 53, 69, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 3,
                            transition: "transform 0.3s ease",
                            "&:hover": { transform: "scale(1.05)" },
                        }}
                    >
                        <Search sx={{ fontSize: 40, color: "#dc3545", opacity: 0.8 }} />
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            color: "#1a1a1a",
                            mb: 1,
                            textAlign: "center",
                            fontSize: "1.5rem",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {en_ja["No Patients Found"] || "No Patients Found"}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "#6b7280",
                            textAlign: "center",
                            mb: 4,
                            maxWidth: "400px",
                            lineHeight: 1.6,
                        }}
                    >
                        {searchTerm || patientType || gender || dateRange || lastVisit
                            ? (en_ja["No patients match your current filters. Try adjusting your search criteria."] ||
                               "No patients match your current filters. Try adjusting your search criteria.")
                            : (en_ja["No patients have been added to the system yet."] ||
                               "No patients have been added to the system yet.")}
                    </Typography>
                    <Button
                        onClick={handleResetFilters}
                        variant="outlined"
                        sx={{
                            borderColor: "#dc3545",
                            color: "#dc3545",
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            textTransform: "none",
                            borderRadius: "12px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                backgroundColor: "#dc3545",
                                color: "white",
                                transform: "translateY(-2px)",
                                boxShadow: "0 8px 24px rgba(220, 53, 69, 0.4)",
                            },
                        }}
                    >
                        {en_ja["Clear All Filters"] || "Clear All Filters"}
                    </Button>
                </Box>
            )}
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => setPatientDialogOpen(false)}
                patientData={selectedPatientData?.user}
                appointmentData={selectedPatientData}
            />
        </Box>
    );
};
export default PatientManagement;
